// Canonical English string catalog. Every user-facing string in the app lives
// here (or in uk.ts). Parameterized entries are functions so each language
// controls its own word order and plural forms.

const plural = (n: number, one: string, other: string) => (n === 1 ? one : other);

export const en = {
  common: {
    loading: "Loading…",
    back: "Back",
    cancel: "Cancel",
    delete: "Delete",
    close: "Close",
    download: "Download",
    edit: "Edit",
    original: "Original",
    noImageAvailable: "No image available",
    zoomHint: "Pinch to zoom • Double-tap to reset",
    deleteImageTitle: "Delete Image?",
    deleteVersionMessage:
      "This will delete this edit version. The original and other versions will remain.",
    deletePermanentMessage:
      "This will permanently delete this image and all its edit history.",
  },
  // Keyed by the canonical color name stored in the database (colors.ts).
  colorNames: {
    Black: "Black",
    White: "White",
    Red: "Red",
    Emerald: "Emerald",
    Navy: "Navy",
    "Blush Pink": "Blush Pink",
    Lavender: "Lavender",
    Champagne: "Champagne",
  } as Record<string, string>,
  gate: {
    title: "Admin access",
    subtitle: "Enter the password to open this page.",
    passwordPlaceholder: "Password",
    submit: "Enter",
    checking: "Checking…",
    wrongPassword: "Wrong password. Please try again.",
  },
  header: {
    openCreatePanel: "Open create panel",
  },
  sidebar: {
    title: "Create Image",
    subtitle: "AI-powered generation",
    tryOnLinks: "Try-On Links",
    prompt: "Prompt",
    promptPlaceholder: "Describe the image you want to generate...",
    referenceImage: "Reference Image (optional)",
    referenceAlt: (n: number) => `Reference ${n}`,
    addMore: (n: number, max: number) => `Add more (${n}/${max})`,
    dropOrBrowse: "Drop images or tap to browse",
    aspectRatio: "Aspect Ratio",
    model: "Model",
    modelFast: "Fast",
    modelHighQuality: "High quality",
    generating: "Generating...",
    generateImage: "Generate Image",
  },
  grid: {
    noImagesTitle: "No images yet",
    noImagesHint: "Enter a prompt in the sidebar to generate your first image",
  },
  card: {
    generationFailed: "Generation failed",
    waitingToStart: "Waiting to start...",
    generating: "Generating...",
    deleteConfirm: "Delete this image?",
  },
  imageModal: {
    title: "Image Details",
    editHistory: "Edit History",
    editApplied: "Edit Applied",
    prompt: "Prompt",
    referenceImage: "Reference Image",
    referenceImages: (n: number) => `Reference Images (${n})`,
  },
  editModal: {
    title: "Edit Image",
    editHistory: "Edit History",
    appliedEdit: "Applied Edit",
    originalPrompt: "Original Prompt",
    describeYourEdit: "Describe Your Edit",
    editPlaceholder: "e.g., Make the sky purple, add a rainbow...",
    applyingEdit: "Applying edit...",
    applyEdit: "Apply Edit",
  },
  admin: {
    backToGenerator: "Back to generator",
    title: "Try-On Links",
    subtitle: "Create a shareable link, send it to a client, watch their try-ons appear here.",
    generationPrompt: "Generation prompt",
    generationPromptNote: "used for every try-on; color instruction is appended automatically",
    saved: "Saved ✓",
    savePrompt: "Save prompt",
    resetToDefault: "Reset to default",
    availableColors: "Available dress colors",
    colorsShown: (n: number, total: number) => `${n} of ${total} shown on try-on pages`,
    colorsNote:
      "Clients always see “As is”; toggled-off colors disappear from their page immediately.",
    loadingSessions: "Loading sessions…",
    sessionsHeading: (n: number) => `Sessions (${n})`,
    nobodyOpened: "Nobody has opened this link yet.",
    unknownDevice: "Unknown device",
    otherDevice: "Other",
    firstSeen: (date: string) => `first seen ${date}`,
    lastActive: (date: string) => `last active ${date}`,
    triesUsed: (n: number) => `${n} ${plural(n, "try", "tries")}`,
    consentedAt: (date: string) => `Consented ${date}`,
    noConsentRecorded: "No consent recorded",
    consentYes: "consent ✓",
    consentNo: "consent —",
    generatedImages: (n: number) => `Generated images (${n})`,
    changeTryLimit: "Change try limit",
    triesNoLimit: (used: number) => `${used} ${plural(used, "try", "tries")} · no limit`,
    triesOf: (used: number, max: number) => `${used} / ${max} tries`,
    titlePlaceholder: "Title, e.g. “Olena — spring collection”",
    triesLabel: "Tries",
    creating: "Creating…",
    generateLink: "Generate link",
    preselectedDressAlt: "Preselected dress",
    removeDress: "Remove dress",
    preselectDress: "+ Preselect a dress (optional) — client can still replace it",
    noLinksYet: "No links yet. Create the first one above.",
    paused: "paused",
    sessionCount: (n: number) => `${n} ${plural(n, "session", "sessions")}`,
    createdAt: (date: string) => `Created ${date}`,
    noTryOnsYet: "no try-ons yet",
    tryOnsCompleted: (completed: number, total: number) =>
      `${completed}/${total} try-ons completed`,
    lastActivity: (date: string) => `last activity ${date}`,
    colorsTried: (colors: string) => `Colors tried: ${colors}`,
    generationCost: (amount: string) => `generation cost ≈ ${amount}`,
    deleteLink: "Delete",
    deleteLinkTitle: "Delete this link?",
    deleteLinkMessage: (title: string) =>
      `This permanently deletes “${title}” — the link, all generated images, client photos, and session history. This cannot be undone.`,
    deletingLink: "Deleting…",
    pause: "Pause",
    resume: "Resume",
    copied: "Copied ✓",
    copyLink: "Copy link",
    hideDetails: "Hide details",
    sessionsAndImages: "Sessions & images",
    recentTryOnAlt: "Recent try-on",
    tryOnAlt: "Try-on",
  },
  tryon: {
    consentTitle: "Before you start",
    consentBody:
      "To show the dress on you, the photos you upload are processed by an AI service and stored so that you and the boutique that sent you this link can see the results. They are used for nothing else — no advertising, no AI training, and they are deleted on request.",
    consentAgreePrefix:
      "I agree to my photos being processed to create virtual try-on images, as described in the ",
    termsNoticeLink: "Terms & Privacy Notice",
    agreeContinue: "Agree & continue",
    tapToReplace: "Tap to replace",
    ourPick: "Our pick — tap to change",
    yourPhoto: "Your photo",
    yourPhotoHint: "Full body works best",
    theDress: "The dress",
    theDressHint: "Photo or screenshot",
    errorNoTries: "You've used all your tries for this link.",
    errorLinkPaused: "This link has been paused.",
    errorGeneric: "Something went wrong. Please try again.",
    linkNotFoundTitle: "This link doesn’t exist",
    linkNotFoundHint: "Please check the address or ask for a new link.",
    linkPausedTitle: "This link is paused",
    linkPausedHint: "Please contact the person who sent it to you to turn it back on.",
    subtitle: (needsDress: boolean) =>
      `Add your photo${needsDress ? " and a dress" : ""} — we’ll show the dress on you. It takes about 20 seconds.`,
    noTriesLeft: "No tries left",
    triesLeft: (left: number, max: number) => `${left} of ${max} tries left`,
    dressColor: "Dress color",
    asIs: "As is",
    uploading: "Uploading…",
    generating: "Generating…",
    go: "Go ✨",
    duplicateWarning:
      "You’ve already generated a look for this photo and color combination — it’s in “Your looks” below.",
    generateAnyway: "Generate anyway",
    usedAllTries: "You’ve used all your tries. Ask the person who sent you this link for more.",
    creatingLook: "Creating your look — usually takes ~20 seconds…",
    yourLooks: "Your looks",
    tryOnResultAlt: "Try-on result",
    resultFailed: "This one didn’t work out — please try again.",
    resultColor: (name: string) => `Color: ${name}`,
    footerNote: "Your photos are used only to create try-on images.",
    termsShort: "Terms & Privacy",
  },
};

export type Translations = typeof en;
